import { useTranslation } from "react-i18next";
import { ReactNode } from 'react';
import { Box, ButtonGroup, Divider, Heading } from '@hawkins/components';
import { Dimensions } from '@hawkins/variables';

import { BackButton } from './BackButton';
import { TranslationNamespace } from "../types";
import { usePageStyles } from "./Page.styles";

export interface PageProps {
  children: ReactNode;
  // Whether to disable the navigation bar at the top of the page.
  disableNav?: boolean;
  // Optional heading text to display at the top of the page.
  headingText?: string;
  // Whether to show a back button in the top flex-start of the page.
  showBack?: boolean;
  // The path to go to when back is clicked.
  backPath?: string;
  // Number of columns
  columns?: number;
  // Render function used to provide a footer with a flex-end aligned button.
  renderFooterButton?: () => ReactNode;
}

/**
 * A wrapper that applies some general layout and styles used on every page.
 */
export function Page({
  children,
  headingText,
  renderFooterButton,
  showBack = false,
  backPath = '',
  columns = 1,
}: PageProps) {
  const { t } = useTranslation(TranslationNamespace.LEP);
  const classes = usePageStyles({ showBack, columns });

  return (
    <main className={classes.main} style={{ backgroundColor: '#f7f7f7' }}>
      <article className={classes.container}>
        {showBack && <BackButton path={backPath} />}
        {headingText && (
          <Heading
            className={classes.nestedHeading}
            gutterBottom={Dimensions.Space16}
            gutterTop={Dimensions.Space24}
          >
            {headingText}
          </Heading>
        )}
        <div style={{ paddingBottom: Dimensions.Space32 }}>
          <Box className={classes.titleBox}>
            <img height="86" src="/images/netflix-arch-logo.svg" />
            <Heading gutterTop={Dimensions.Space24} level={3}>
              {t('common_law_enforcement_request_portal')}
            </Heading>
          </Box>
          <Box className={classes.contentBox}>
            {children}
          </Box>
        </div>
        {renderFooterButton && (
          <footer>
            <Divider
              layout={{
                marginBottom: Dimensions.Space24,
              }}
            />
            <ButtonGroup justifyContent="flex-end">
              {renderFooterButton()}
            </ButtonGroup>
          </footer>
        )}
      </article>
    </main>
  );
}
